// components/recurringDonationWidget.jsx
import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectSelectedCurrency } from '../../store/user';
import { selectProducts } from '../../store/products';

import TimeZoneSelector from './timeZoneSelector';
import SelectCurrency from '../general/selectCurrency';
import ProductSelector from './productSelector';

import { calculateRecurringDonations } from '../../utils/recurringDonationUtils';
import RecurringDonationCalendar from './recurringDonationCalendar';
import RecurringSummary from './recurringSummary';

const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString(),
}));

const MONTHS_OF_YEAR = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// LocalStorage key
const STORAGE_KEY = 'RECURRING_WIDGET_DATA';

/**
 * A widget that supports "Daily / Weekly / Monthly / Yearly" recurrences
 * for building a future donation schedule, and saves/loads user selections from localStorage.
 */
const RecurringDonationWidget = (props) => {
  const { productsList = [] } = props;

  const selectedCurrency = useSelector(selectSelectedCurrency);
  const productsFromStore = useSelector(selectProducts);

  const [allProducts, setAllProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);

  // TimeZone & Currency
  const [timeZone, setTimeZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

  // Recurrence dropdowns
  const [recurrence, setRecurrence] = useState('daily');
  const [dayValue, setDayValue] = useState('');    // could be day-of-week or day-of-month
  const [monthValue, setMonthValue] = useState(''); // used only if recurrence === 'yearly'

  // Donation schedule & summary
  const [donationSchedule, setDonationSchedule] = useState([]);
  const [summary, setSummary] = useState({
    totalAmount: 0,
    totalShares: 0,
    shareValue: 0,
  });

  const calendarRef = useRef(null);

  /* -------------------------- Load the products from store -------------------------- */
  useEffect(() => {
    if (productsFromStore && Object.keys(productsFromStore).length) {
      const temp = [];
      Object.values(productsFromStore).forEach((category) => {
        if (category.sub_categories) {
          category.sub_categories.forEach((subCat) => {
            subCat.products.forEach((p) => {
              if (Number(p.status) === 1) {
                if (productsList.includes(p.creator)) {
                  temp.push(p);
                }
              }
            });
          });
        }
      });
      setAllProducts(temp);
    }
  }, [productsFromStore, productsList]);

  /* ------------------------- Load user selections from localStorage on mount ------------------------- */
  useEffect(() => {
    const storedData = localStorage.getItem(STORAGE_KEY);
    if (storedData) {
      const parsedData = JSON.parse(storedData);

      if (parsedData.timeZone) setTimeZone(parsedData.timeZone);
      if (parsedData.recurrence) setRecurrence(parsedData.recurrence);
      if (parsedData.dayValue) setDayValue(parsedData.dayValue);
      if (parsedData.monthValue) setMonthValue(parsedData.monthValue);
      if (parsedData.selectedProducts) setSelectedProducts(parsedData.selectedProducts);
      if (parsedData.donationSchedule) setDonationSchedule(parsedData.donationSchedule);
      if (parsedData.summary) setSummary(parsedData.summary);
    }
  }, []);

  /* ------------------------- Recompute schedule whenever inputs change ------------------------- */
  useEffect(() => {
    if (selectedProducts.length === 0) {
      setDonationSchedule([]);
      setSummary({ totalAmount: 0, totalShares: 0, shareValue: 0 });
      return;
    }

    const { donationSchedule: sched, summary: newSummary } = calculateRecurringDonations({
      timeZone,
      selectedProducts,
      currency: selectedCurrency,
      recurrence,
      dayValue,
      monthValue,
      occurrences: 30,
    });

    setDonationSchedule(sched);
    setSummary(newSummary);
  }, [timeZone, selectedProducts, selectedCurrency, recurrence, dayValue, monthValue]);

  /* ------------------------- Save user selections to localStorage on each change ------------------------- */
  useEffect(() => {
    const dataToStore = {
      timeZone,
      recurrence,
      dayValue,
      monthValue,
      selectedProducts,
      donationSchedule,
      summary,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
  }, [
    timeZone,
    recurrence,
    dayValue,
    monthValue,
    selectedProducts,
    donationSchedule,
    summary,
  ]);

  /* ------------------------- Handlers for recurrences ------------------------- */
  const handleRecurrenceChange = (e) => {
    const val = e.target.value;
    setRecurrence(val);
    // Reset dayValue & monthValue each time
    setDayValue('');
    setMonthValue('');
  };

  const handleDayChange = (e) => {
    setDayValue(e.target.value);
  };

  const handleMonthChange = (e) => {
    setMonthValue(e.target.value);
  };

  // Decide if "Day" dropdown is hidden
  const showDayDropdown = recurrence !== 'daily';

  // For weekly => day-of-week
  // For monthly/yearly => day-of-month
  let dayOptions = [];
  if (recurrence === 'weekly') {
    dayOptions = DAYS_OF_WEEK;
  } else if (recurrence === 'monthly' || recurrence === 'yearly') {
    dayOptions = DAYS_OF_MONTH;
  }

  // Show "Month" only if yearly
  const showMonthDropdown = recurrence === 'yearly';

  return (
    <div className="w-full scrollarea min-h-[600px]">
      <div className="w-full py-2 md:py-4 flex flex-wrap items-start">
        {/* LEFT SIDE */}
        <div className="basis-full lg:basis-2/3 px-4 md:px-4 flex flex-col gap-4 md:gap-6">
          <SectionTitle count={1} text="Select Time Zone" />
          <div className="w-full md:w-2/3 flex gap-2">
            <TimeZoneSelector timeZone={timeZone} setTimeZone={setTimeZone} />
            <div className="w-1/2 md:w-full">
              <label className="text-stone-500 flex items-center">Currency</label>
              <div className="cursor-pointer relative w-full rounded-md h-12 border border-[#282828]" id="ramadan-daily-currency">
                <SelectCurrency onChangeCurrency={() => {}} className="h-full [&>div]:h-full-" />
              </div>
            </div>
          </div>

          <SectionTitle count={2} text="Recurrence Options" />

          <div className="w-full flex gap-2 mb-4">
            <div className="w-full md:w-1/3">
              <label className="text-stone-500 flex items-center">Recurring:</label>
              <div className="cursor-pointer relative w-full rounded-md h-12 border border-[#282828]">
                <select
                  className="px-3 py-2 w-full rounded-md cursor-pointer h-full"
                  value={recurrence}
                  onChange={handleRecurrenceChange}
                >
                  {RECURRENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {showMonthDropdown && (
              <div className="w-full md:w-1/3">
                <label className="text-stone-500 flex items-center">Month:</label>
                <div className="cursor-pointer relative w-full rounded-md h-12 border border-[#282828]">
                  <select
                    className="px-3 py-2 w-full rounded-md cursor-pointer h-full"
                    value={monthValue}
                    onChange={handleMonthChange}
                  >
                    <option value="">Select a month</option>
                    {MONTHS_OF_YEAR.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {showDayDropdown && (
              <div className="w-full md:w-1/3">
                <label className="text-stone-500 flex items-center">
                  {recurrence === 'weekly' ? 'Day of Week:' : 'Day of Month:'}
                </label>
                <div className="cursor-pointer relative w-full rounded-md h-12 border border-[#282828]">
                  <select
                    className="px-3 py-2 w-full rounded-md cursor-pointer h-full"
                    value={dayValue}
                    onChange={handleDayChange}
                  >
                    <option value="">Select a day</option>
                    {dayOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          <SectionTitle count={3} text="Choose Your Cause" />
          <ProductSelector
            products={allProducts}
            selectedProducts={selectedProducts}
            setSelectedProducts={setSelectedProducts}
            currency={selectedCurrency}
          />
        </div>

        {/* RIGHT SIDE */}
        <div className="basis-full lg:basis-1/3 px-2 md:px-4 sticky top-28">
          <RecurringSummary
            selectedProducts={selectedProducts}
            donationSchedule={donationSchedule}
            timeZone={timeZone}
            recurrence={recurrence}
            dayValue={dayValue}
            monthValue={monthValue}
            numberOfMultiplierNights={0}
            numberOfNights={donationSchedule.length}
            selectedStrategies={[]}
            totalAmount={summary.totalAmount}
            currency={selectedCurrency}
            viewCalendar={() =>
              window.scrollTo({
                behavior: 'smooth',
                top: calendarRef.current.offsetTop - 100,
              })
            }
            selectedConditions={[]}
            donationOption="equal"
            monthsOfYear={MONTHS_OF_YEAR}
          />
        </div>
      </div>

      <div className="w-full mt-4 py-2 md:py-4 px-0 md:px-4" id="calendar" ref={calendarRef}>
        <RecurringDonationCalendar
          donationSchedule={donationSchedule}
          currency={selectedCurrency}
          timeZone={timeZone}
          totalAmount={summary.totalAmount}
          recurrence={recurrence}
          // no need for ramadanStartDate
          // numberOfNights={donationSchedule.length}
        />
      </div>
    </div>
  );
};

export default RecurringDonationWidget;


/** A simple reused component for section headers */
const SectionTitle = ({ count = 0, text = '' }) => {
  return (
    <div className="w-full flex items-center border border-[#253B7E] bg-white rounded-full p-2 md:p-4 shadow-md">
      <div className="text-white bg-[#F60362] rounded-full w-10 h-10 md:w-14 md:h-14 min-w-[40px] flex justify-center items-center font-bold text-lg md:text-2xl mr-2 md:mr-4">
        {count}
      </div>
      <p className="text-lg md:text-xl text-[#253B7E] font-bold">{text}</p>
    </div>
  );
};
